/**
 * Carbon accordion: rows split by border-subtle lines, each a 40px header
 * button (title, optional helper `meta`, a trailing chevron that turns on
 * open) over its content. Each header is a disclosure: aria-expanded, and
 * aria-controls pointing at its region. Several rows can be open at once.
 * `lazy` mounts a panel's content only while open; `flush` drops the content
 * inset so full-width rows (a policy list) run edge to edge.
 */
import { useId, useState, type ReactNode } from 'react'
import clsx from 'clsx'
import { IconChevronDown } from './icons'

export interface AccordionItem {
  id: string
  title: ReactNode
  /** Helper text before the chevron ("12 controls"). */
  meta?: ReactNode
  content: ReactNode
}

export interface AccordionProps {
  items: AccordionItem[]
  /** Ids open on first render. */
  defaultOpen?: string[]
  /** Render a panel's content only while it is open (long lists stay unmounted when collapsed). */
  lazy?: boolean
  /** Content runs full width with no inset, under a border-subtle line: rows that bring their own padding. */
  flush?: boolean
  /** The headers' heading element. Default h3; h4 when the accordion sits under an h3 (a framework in the BPA deep dive). */
  headingLevel?: 'h2' | 'h3' | 'h4'
  className?: string
}

export function Accordion({ items, defaultOpen, lazy = false, flush = false, headingLevel: Heading = 'h3', className }: AccordionProps) {
  const baseId = useId()
  const [open, setOpen] = useState<Set<string>>(() => new Set(defaultOpen ?? []))
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  return (
    <ul className={clsx('m-0 list-none border-b border-line-subtle p-0', className)}>
      {items.map((item) => {
        const isOpen = open.has(item.id)
        const panelId = `${baseId}-${item.id}`
        return (
          <li key={item.id} className="border-t border-line-subtle">
            <Heading className="m-0">
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggle(item.id)}
                className="flex min-h-10 w-full items-center gap-4 px-4 py-2 text-left text-body text-text-primary transition-colors duration-110 ease-carbon hover:bg-layer-hover"
              >
                <span className="min-w-0 flex-1">{item.title}</span>
                {item.meta ? <span className="flex-none text-helper text-text-helper">{item.meta}</span> : null}
                <IconChevronDown className={clsx('text-text-primary transition-transform duration-150 ease-carbon', isOpen && 'rotate-180')} />
              </button>
            </Heading>
            <div
              id={panelId}
              role="region"
              aria-label={typeof item.title === 'string' ? item.title : undefined}
              hidden={!isOpen}
              className={flush ? 'border-t border-line-subtle' : 'px-4 pb-6 pt-2 text-body text-text-secondary c-md:pr-[25%]'}
            >
              {lazy && !isOpen ? null : item.content}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

export default Accordion
